import React, { useState, useEffect } from 'react';
import { Tag as TagIcon, Plus, X, Check, Edit2, Trash } from 'lucide-react';
import { useTagStore } from '../stores/tag-store';
import { useFileStore } from '../stores/file-store';
import FileGrid from '../components/files/FileGrid';
import CreateTagModal from '../components/tags/CreateTagModal';
import ConfirmDialog from '../components/ConfirmDialog';
import { useI18n } from '../i18n';

const TagsPage: React.FC = () => {
  const { tags, selectedTag, selectTag, isLoading, fetchTags, updateTag, deleteTag } = useTagStore();
  const [isEditing, setIsEditing] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  
  const { files, filteredFiles, toggleTagSelection, selectedTags, search } = useFileStore();
  const { t } = useI18n();
  
  useEffect(() => {
    fetchTags();
  }, [fetchTags]);
  
  const handleTagSelect = (tag: Tag) => {
    if (selectedTag?.id === tag.id) {
      // Deselect if already selected
      selectTag(null);
      toggleTagSelection(tag);
    } else {
      selectTag(tag);
      toggleTagSelection(tag);
    }
  };
  
  const filesWithSelectedTag = selectedTag 
    ? files.filter(file => file.tags.some(tag => tag.id === selectedTag.id))
    : [];
  
  const handleEditTag = () => {
    setIsEditing(true);
    if (selectedTag) {
      setNewTagName(selectedTag.name);
    }
  };
  
  const handleSaveTagEdit = async () => {
    if (!selectedTag || !newTagName.trim()) return;
    
    try {
      await updateTag(selectedTag.id, { name: newTagName.trim() });
      setIsEditing(false);
    } catch (error) {
      console.error('Error updating tag:', error);
    }
  };
  
  const handleDeleteTag = () => {
    if (!selectedTag) return;
    setShowDeleteConfirm(true);
  };

  const doDeleteTag = async () => {
    if (!selectedTag) return;
    setShowDeleteConfirm(false);
    try {
      await deleteTag(selectedTag.id);
    } catch (error) {
      console.error('Error deleting tag:', error);
    }
    setIsEditing(false);
  };
  
  const handleCancelTagEdit = () => {
    setIsEditing(false);
  };
  
  const colorOptions = [
    '#ef4444', // red
    '#f59e0b', // amber
    '#10b981', // emerald
    '#3b82f6', // blue
    '#8b5cf6', // purple
    '#ec4899', // pink
    '#6b7280', // gray
    '#14b8a6', // teal
  ];
  
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-gray-800">{t('tagsTitle')}</h1>
        <button 
          onClick={() => setShowCreateModal(true)}
          className="flex items-center px-4 py-2 text-sm font-medium text-white transition-colors bg-teal-600 rounded-md hover:bg-teal-700 focus:outline-none focus:ring-2 focus:ring-teal-500 focus:ring-opacity-50"
        >
          <Plus size={16} className="mr-2" />
          {t('newTag')}
        </button>
      </div>
      
      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="px-4 py-3 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-800">{t('allTags')}</h2>
          </div>
          <div className="p-4">
            {isLoading && tags.length === 0 ? (
              <div className="flex justify-center p-4">
                <div className="w-6 h-6 border-2 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => (
                <button
                  key={tag.id}
                  onClick={() => handleTagSelect(tag)}
                  className={`flex items-center px-3 py-2 text-sm rounded-md transition-colors ${
                    selectedTag?.id === tag.id
                      ? 'bg-teal-100 text-teal-800'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <span
                    className="w-3 h-3 mr-2 rounded-full"
                    style={{ backgroundColor: tag.color }}
                  ></span>
                  {tag.name}
                </button>
              ))}
              
              <button 
                onClick={() => setShowCreateModal(true)}
                className="flex items-center px-3 py-2 text-sm rounded-md bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                <Plus size={16} className="mr-2" />
                {t('addTag')}
              </button>
            </div>
            )}
          </div>
        </div>
        
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm lg:col-span-2">
          {selectedTag ? (
            <>
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                {isEditing ? (
                  <div className="flex items-center space-x-2">
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value)}
                      className="px-2 py-1 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-teal-500"
                    />
                    <div className="flex items-center">
                      <button
                        onClick={handleSaveTagEdit}
                        className="p-1 text-teal-600 hover:text-teal-800"
                      >
                        <Check size={16} />
                      </button>
                      <button
                        onClick={handleCancelTagEdit}
                        className="p-1 text-gray-500 hover:text-gray-700"
                      >
                        <X size={16} />
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center">
                    <div
                      className="flex items-center justify-center w-8 h-8 mr-3 rounded-md"
                      style={{ backgroundColor: `${selectedTag.color}20` }}
                    >
                      <TagIcon size={16} style={{ color: selectedTag.color }} />
                    </div>
                    <h2 className="text-lg font-medium text-gray-800">{selectedTag.name}</h2>
                  </div>
                )}
                
                {!isEditing && (
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={handleDeleteTag}
                      className="p-1.5 text-gray-500 rounded-md hover:bg-gray-100 hover:text-gray-700"
                    >
                      <Trash size={16} className="text-red-500" />
                    </button>
                    <button className="p-1.5 text-gray-500 rounded-md hover:bg-gray-100 hover:text-gray-700">
                      <Trash size={16} />
                    </button>
                  </div>
                )}
              </div>
              
              <div className="p-4">
                <div className="mb-4">
                  <h3 className="mb-2 text-sm font-medium text-gray-700">{t('tagColor')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {colorOptions.map((color) => (
                      <button
                        key={color}
                        className={`w-6 h-6 rounded-full ${
                          color === selectedTag.color ? 'ring-2 ring-offset-2 ring-teal-500' : ''
                        }`}
                        style={{ backgroundColor: color }}
                      />
                    ))}
                  </div>
                </div>
                
                <div>
                  <h3 className="mb-2 text-sm font-medium text-gray-700">{t('filesWithThisTag')}</h3>
                  <FileGrid 
                    files={filesWithSelectedTag} 
                    emptyMessage={t('noFilesWithThisTag')}
                  />
                </div>
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center p-8 text-center">
              <TagIcon size={48} className="mb-4 text-gray-300" />
              <p className="text-gray-500">{t('selectTagToView')}</p>
            </div>
          )}
        </div>
      </div>
      
      {showCreateModal && (
        <CreateTagModal onClose={() => setShowCreateModal(false)} />
      )}

      <ConfirmDialog
        open={showDeleteConfirm}
        title="删除标签"
        message={`确定要删除标签 "${selectedTag?.name}" 吗？此操作不可撤销。`}
        variant="danger"
        confirmText="删除"
        onConfirm={doDeleteTag}
        onCancel={() => setShowDeleteConfirm(false)}
      />
    </div>
  );
};

export default TagsPage;